import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/leads";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_URL = "https://readdy.ai/api/form/dahgi9joh653ivfvnuk0";

const materialOptions = ["文字描述", "聊天记录", "付款凭证", "合同文件", "照片或影像", "其他材料"];

export default function SubmissionForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formName", "蝌蚪爆料投稿表单");

    const honeypot = String(formData.get("contact_alt") ?? "").trim();

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    if (honeypot) {
      setStatus("success");
      form.reset();
      setDetailLength(0);
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = !!parsed && String(parsed.code).toUpperCase() === "OK";

      if (response.ok && isOk && !isSpam) {
        setStatus("success");
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试，或通过邮箱与我们联系。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请检查网络后重试。");
    }
  };

  return (
    <form
      id="kedou-submission-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8"
    >
      <div className="flex items-start gap-3 rounded-md bg-secondary-50 p-4">
        <i className="ri-shield-user-line mt-0.5 text-lg text-secondary-700" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-foreground-700">
          投稿人信息默认匿名处理。你可以只留下一个可联系的方式，核实编辑会在 48 小时内与你单线沟通。
        </p>
      </div>

      {status === "success" && (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 p-4">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-700" aria-hidden="true" />
          <div>
            <p className="font-heading text-sm font-semibold text-foreground-950">线索已提交</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">
              感谢你的信任。核实编辑会在 48 小时内联系你，沟通方式与呈现范围都由你决定。
            </p>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
          <i className="ri-error-warning-line mt-0.5 text-lg text-primary-600" aria-hidden="true" />
          <div>
            <p className="font-heading text-sm font-semibold text-foreground-950">提交失败</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{formError}</p>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label htmlFor="title" className="mb-2 block text-sm font-medium text-foreground-800">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            maxLength={80}
            placeholder="用一句话概括你要反映的事情"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div>
          <label htmlFor="category" className="mb-2 block text-sm font-medium text-foreground-800">
            线索栏目 <span className="text-primary-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="" disabled>
              请选择最接近的栏目
            </option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
            <option value="其他">其他 / 不确定</option>
          </select>
        </div>

        <div>
          <label htmlFor="region" className="mb-2 block text-sm font-medium text-foreground-800">
            事件发生地区
          </label>
          <input
            id="region"
            name="region"
            type="text"
            maxLength={40}
            placeholder="例如：华东某市 / 可留空"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="detail" className="mb-2 block text-sm font-medium text-foreground-800">
            线索描述 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="detail"
            name="detail"
            required
            maxLength={500}
            rows={6}
            onChange={(e) => setDetailLength(e.target.value.length)}
            placeholder="请描述事件的经过：发生了什么、涉及哪些环节、你了解到的时间与范围。不确定的部分可以标注为「待确认」。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
          <p className="mt-1.5 text-right text-xs text-foreground-500">{detailLength} / 500</p>
        </div>

        <fieldset className="md:col-span-2">
          <legend className="mb-2 block text-sm font-medium text-foreground-800">可提供的材料</legend>
          <div className="flex flex-wrap gap-2">
            {materialOptions.map((m) => (
              <label
                key={m}
                className="flex cursor-pointer items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors has-[:checked]:border-primary-400 has-[:checked]:bg-primary-100 has-[:checked]:text-primary-800"
              >
                <input type="checkbox" name="materials" value={m} className="accent-primary-500" />
                {m}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor="contact" className="mb-2 block text-sm font-medium text-foreground-800">
            联系方式 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact"
            name="contact"
            type="text"
            required
            maxLength={60}
            placeholder="微信 / 手机号 / 其他可联系方式"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
          <p className="mt-1.5 text-xs text-foreground-500">仅核实编辑可见，不会对外公开。</p>
        </div>

        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
            备用邮箱（选填）
          </label>
          <input
            id="email"
            name="email"
            type="email"
            maxLength={80}
            placeholder="用于接收核实进度说明"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="anonymous" className="mb-2 block text-sm font-medium text-foreground-800">
            公开呈现时的署名方式
          </label>
          <select
            id="anonymous"
            name="anonymous"
            defaultValue="完全匿名"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="完全匿名">完全匿名</option>
            <option value="匿名 + 身份概述">匿名，但可标注身份概述（如「前从业者」）</option>
            <option value="可署网名">可署网名</option>
            <option value="发布前再确认">先不决定，发布前与编辑再确认</option>
          </select>
        </div>
      </div>

      <div className="field-verify-block" aria-hidden="true">
        <label htmlFor="contact_alt">Contact alt</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-7 flex flex-col gap-4 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-sm text-xs leading-relaxed text-foreground-500">
          提交即表示你理解：我们会先核实再呈现，无法确认的部分会明确标注状态。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-2-line"} aria-hidden="true" />
          {status === "submitting" ? "正在提交…" : "提交线索"}
        </button>
      </div>
    </form>
  );
}